import React from 'react';

import {
  UilAnalytics,
  UilAngleDown,
  UilAngleRight,
  UilApps,
  UilArrowLeft,
  UilArrowRight,
  UilBooks,
  UilBolt,
  UilBriefcase,
  UilCalendar,
  UilDocumentLayoutLeft,
  UilEdit,
  UilEye,
  UilFileAlt,
  UilHome,
  UilMegaphone,
  UilNewspaper,
  UilPaintTool,
  UilPalette,
  UilPlug,
  UilPresentation,
  UilSearch,
  UilSetting,
  UilShare,
  UilSignout,
  UilSpinner,
  UilThumbsUp,
  UilTimes,
  UilTrashAlt,
  UilUsersAlt,
  UilWindow,
  UilPen,
  UilVideo,
  UilImage,
  UilSort,
  UilShareAlt,
  UilCheck,
  UilQuestionCircle,
  UilCheckCircle,
  UilRedo,
  UilRefresh,
  UilFile,
  UilUpload,
  UilTrash,
  UilPlayCircle,
  UilAngleLeftB,
  UilAngleRightB,
} from '@iconscout/react-unicons';

type UniconComponent = typeof UilHome;

// ICON_MAP is the single source of truth for supported icons.
// IconName and ICON_NAMES are both derived from it — adding an entry here
// is the only change needed to expose a new icon to the rest of the app.
//
// Naming convention:
//   - Use camelCase app-level names, not Unicons provider names.
//   - Prefer visual shape names for generic UI controls (arrowLeft, chevronLeft, caretLeft).
//   - Use product/domain names only when the icon represents a product concept (jobs, analytics).
const ICON_MAP = {
  analytics: UilAnalytics,
  apps: UilApps,
  arrowLeft: UilArrowLeft,
  arrowRight: UilArrowRight,
  audienceResearch: UilUsersAlt,
  books: UilBooks,
  brandGuideline: UilPalette,
  briefcase: UilBriefcase,
  calendar: UilCalendar,
  chevronDown: UilAngleDown,
  chevronRight: UilAngleRight,
  close: UilTimes,
  connectors: UilPlug,
  creativeStudio: UilPaintTool,
  delete: UilTrashAlt,
  documentLayoutLeft: UilDocumentLayoutLeft,
  edit: UilEdit,
  exit: UilSignout,
  home: UilHome,
  insideLook: UilEye,
  jobs: UilBriefcase,
  library: UilBooks,
  myPosts: UilNewspaper,
  advocacy: UilMegaphone,
  search: UilSearch,
  settings: UilSetting,
  share: UilShare,
  showcase: UilPresentation,
  spinner: UilSpinner,
  storyBuilder: UilFileAlt,
  storyOverview: UilWindow,
  thunder: UilBolt,
  thumbsUp: UilThumbsUp,
  widget: UilApps,
  pen: UilPen,
  pdf: UilFileAlt,
  video: UilVideo,
  image: UilImage,
  sort: UilSort,
  shareAlt: UilShareAlt,
  check: UilCheck,
  questionCircle: UilQuestionCircle,
  checkCircle: UilCheckCircle,
  redo: UilRedo,
  refresh: UilRefresh,
  file: UilFile,
  upload: UilUpload,
  trash: UilTrash,
  playCircle: UilPlayCircle,
  angleLeftB: UilAngleLeftB,
  angleRightB: UilAngleRightB,
} satisfies Record<string, UniconComponent>;

export type IconName = keyof typeof ICON_MAP;

export interface IconProps
  extends Omit<React.SVGAttributes<SVGElement>, 'name'> {
  name: IconName;
  size?: number | string;
  ariaLabel?: string;
}

export const ICON_NAMES = Object.keys(ICON_MAP) as IconName[];

const Icon = ({
  name,
  size = 16,
  color = 'currentColor',
  ariaLabel,
  ...rest
}: IconProps): React.JSX.Element => {
  const Component = ICON_MAP[name];

  return (
    <Component size={size} color={color} aria-label={ariaLabel} {...rest} />
  );
};

export default Icon;
